import React, { useCallback } from "react";
import CarCard from "@/components/auctions/CarCard";

import BtnNavigateBack from "@/components/ui/btn_navigate_back";
import { useTheme } from "@/context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useLanguage } from "@/context/LanguageContext";
import { commonMenuLabels } from "@/i18n";
import { PaginatedGrid } from "@/components/ui/paginated-grid";
import { useFavorites } from "@/hooks/useFavorites";
import { useIsMobile } from "@/hooks/use-mobile";
import { buildAuctionListingPath } from "@/lib/listingUrl";
import { resetAuthRefreshState } from "@/integrations/clients/carAuction/carAuctionClient";

const Favorites = () => {
  // TODO: Get userId from auth context instead of hardcoding
  const userId = "user1";
  const navigate = useNavigate();
  const { language } = useLanguage();
  const menuLabels = commonMenuLabels(language);
  const {
    favorites,
    isLoadingFavorites,
    favoritesError,
    refetchFavorites,
    removeFavorite,
  } = useFavorites(userId);

  const { theme } = useTheme();
  const isDark = theme === "dark";
  const isMobile = useIsMobile();

  // Use favorites directly as BaseListing objects
  const filteredFavorites = favorites;

  const handleCardClick = useCallback(
    (listingId: string) => {
      // Find the listing by ID
      const listing = filteredFavorites.find(
        (listing) => listing.id === listingId,
      );
      if (!listing) return;

      navigate(buildAuctionListingPath(listing.campaignId, listing), {
        state: { from: "/favorites" },
      });
    },
    [navigate, filteredFavorites],
  );

  const handleRemoveFavorite = useCallback(
    async (listingId: string) => {
      // Find the listing by ID
      const listing = favorites.find((listing) => listing.id === listingId);
      if (!listing) return;

      // React Query will handle optimistic updates automatically
      try {
        const data = await removeFavorite({
          listingId: listing.id,
          userId: userId,
        });
        // Success: Keep item removed (it will be removed from server data too)
        toast.success(menuLabels.removedFromFavorites);
      } catch (error) {
        // Error: Show error message (React Query will restore the item automatically)
        toast.error(
          menuLabels.favoritesRemoveError ||
            "Could not remove from favorites. Please try again.",
        );
      }
    },
    [favorites, removeFavorite, userId, menuLabels],
  );

  const renderFavoriteCard = useCallback(
    (listing: (typeof filteredFavorites)[0]) => {
      return (
        <CarCard
          key={listing.id}
          listing={listing}
          viewMode="wide"
          onFavoriteToggle={() => handleRemoveFavorite(listing.id)}
        />
      );
    },
    [handleRemoveFavorite],
  );

  if (isLoadingFavorites) {
    return (
      <div className="min-h-screen bg-transparent relative animate-fade-in">
        <div className="container max-w-6xl py-2 px-4 relative z-10">
          {!isMobile && <BtnNavigateBack />}
          <h1
            className={`text-2xl pb-4 font-bold text-center ${
              isDark ? "text-white" : "text-gray-800"
            }`}
          >
            {menuLabels.favoritesTitle || "Favorites"}
          </h1>
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <div className="text-lg">
                {menuLabels.favoritesLoading || "Loading favorites..."}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (favoritesError) {
    return (
      <div className="min-h-screen bg-transparent relative animate-fade-in">
        <div className="container max-w-6xl py-2 px-4 relative z-10">
          <div className="flex items-center justify-center h-64">
            <div className="text-lg text-red-500">
              {menuLabels.favoritesErrorFetching ||
                "An error occurred while loading favorites."}
              <button
                onClick={() => {
                  resetAuthRefreshState();
                  void refetchFavorites();
                }}
                className="ml-2 underline"
              >
                {menuLabels.tryAgain || "Try again"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-transparent relative animate-fade-in">
      <div className="container max-w-6xl py-2 px-4 relative z-10">
        {!isMobile && <BtnNavigateBack />}

        <h1
          className={`text-2xl pb-4 font-bold text-center ${
            isDark ? "text-white" : "text-gray-800"
          }`}
        >
          {menuLabels.favoritesTitle || "Favorites"}
        </h1>

        <PaginatedGrid
          data={filteredFavorites}
          renderItem={renderFavoriteCard}
          emptyMessage={menuLabels.favoritesEmpty || "No favorites yet"}
        />
      </div>
    </div>
  );
};

export default Favorites;
